import { Box, Button, Grid, Paper, Stack, TablePagination } from '@mui/material'
import Breadcrumbs from 'components/Breadcrumbs'
import Label from 'components/Label'
import { useDownload } from 'hooks/download.hook'
import { usePagination } from 'hooks/pagination.hook'
import Layout from 'layouts/Default'
import React, { useCallback, useEffect, useState } from 'react'
import hearingService from 'services/hearing.service'
import {
  HearingFilterForm,
  HearingFilterRequest,
  HearingFilterResponse,
  HearingFilterSchema,
} from 'types/search-hearing.type'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { TableData } from './components/TableData'
import { FilterHearingOptions } from './enums/filter.enum'
import { StyledBoxContainerTitle as ContainerTitle } from './styles'
import { useSnackBar } from 'contexts/snackbar.context'
import { useBackdrop } from 'contexts/backdrop.context'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'
import FormControlRadio, { RadioOption } from 'components/FormControlRadio'
import { FormProvider, useForm } from 'react-hook-form'
import FormInput from 'components/FormInput'
import FormInputDatePicker from 'components/FormInputDatePicker'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { isFilled } from 'utils/validate.util'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'

const formInit: HearingFilterForm = {
  type: FilterHearingOptions.RASCUNHO_OITIVA,
  name: '',
  dateFrom: null,
  dateTo: null,
  responsibleFor: '',
  cnjNumber: '',
}

const SearchHearing: React.FC = () => {
  const navigate = useNavigate()
  const {
    page,
    elements: { totalOfElements, handleTotalOfElements },
    rows: { rowsPerPage, rowsOptions },
    handleNewPage,
    handleRowsPerPage,
  } = usePagination()

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const form = useForm<HearingFilterForm>({
    mode: 'onSubmit',
    resolver: zodResolver(HearingFilterSchema),
    defaultValues: formInit,
  })

  const { control, watch, reset, handleSubmit } = form

  const typeWatch = watch('type')

  const formNotValid =
    !isFilled(watch('name')) &&
    !isFilled(watch('dateFrom')) &&
    !isFilled(watch('dateTo')) &&
    !isFilled(watch('responsibleFor')) &&
    !isFilled(watch('cnjNumber'))

  const [data, setData] = useState<HearingFilterResponse[] | undefined>(undefined)
  const [showMessage, setShowMessage] = useState(false)

  const handleFillRadioOptions: RadioOption[] = [
    {
      value: FilterHearingOptions.RASCUNHO_OITIVA,
      label: 'Rascunho da Oitiva',
    },
    {
      value: FilterHearingOptions.OITIVA_FINALIZADA,
      label: 'Oitiva Finalizada',
    },
    {
      value: FilterHearingOptions.OITIVA_EXCUIDA,
      label: 'Oitiva Excluída',
    },
  ]

  const handleReset = (): void => {
    setData(undefined)
    setShowMessage(false)
    reset(formInit)
    handleNewPage(null, 0)
  }

  const handleSubmitForm = async (filter?: HearingFilterForm): Promise<void> => {
    if (formNotValid) {
      setShowMessage(true)
      return
    }

    await buscar(filter)
  }

  const handleDelete = async (hearingId?: number): Promise<void> => {
    if (hearingId) {
      await hearingService
        .deleteById(hearingId)
        .then(async (response) => {
          if (response) {
            await handleSubmit(buscar)()
            showSnackBar(SystemMessages.MESSAGE_024, 'success')
          }
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const handleReactivate = async (hearingId?: number): Promise<void> => {
    if (hearingId) {
      await hearingService
        .reactivateById(hearingId)
        .then(async (response) => {
          if (response) {
            await handleSubmit(buscar)()
            showSnackBar(SystemMessages.MESSAGE_027, 'success')
          }
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const handleDownloadHearing = async (hearingId?: number): Promise<void> => {
    navigate(`/download-oitiva/${hearingId}`)
  }

  const buscar = useCallback(
    async (filter?: HearingFilterForm): Promise<void> => {
      const filters: HearingFilterRequest = {
        name: filter?.name,
        dateFrom: filter?.dateFrom,
        dateTo: filter?.dateTo,
        responsibleFor: filter?.responsibleFor,
        cnjNumber: filter?.cnjNumber,
        type: filter?.type,
        pageSize: rowsPerPage,
        page,
      }
      handleBackdropOpen()
      await hearingService
        .getByFilters(filters)
        .then((response) => {
          setData(response.data.content)
          handleTotalOfElements(response.data.totalElements)
          setShowMessage(false)
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    },
    [rowsPerPage, page],
  )

  const handlePage = async (newPage: number, newRowsPerPage?: any) => {
    handleNewPage(null, newPage)
    if (newRowsPerPage) {
      handleRowsPerPage(newRowsPerPage)
    }
  }

  const handleResponsavelLabel = (value?: string | null) => {
    switch (value) {
      case FilterHearingOptions.OITIVA_FINALIZADA:
        return 'Responsável pela finalização'
      case FilterHearingOptions.OITIVA_EXCUIDA:
        return 'Responsável pela exclusão'
      default:
        return 'Responsável pela inclusão'
    }
  }

  const handleDataInicioLabel = (value?: string | null) => {
    switch (value) {
      case FilterHearingOptions.OITIVA_EXCUIDA:
        return 'Data de exclusão'
      default:
        return 'Data de inclusão'
    }
  }

  const handleResponsavelPlaceholder = (value?: string | null) => {
    switch (value) {
      case FilterHearingOptions.OITIVA_FINALIZADA:
        return 'Informe o responsável pela finalização'
      case FilterHearingOptions.OITIVA_EXCUIDA:
        return 'Informe o responsável pela exclusão'
      default:
        return 'Informe o responsável pela inclusão'
    }
  }

  useEffect(() => {
    ;(async () => {
      if (!formNotValid) {
        await handleSubmit(async (filter) => await buscar(filter))()
      }
    })()
  }, [buscar])

  return (
    <Layout>
      <Grid container item xs={12} sx={{ mb: 2 }}>
        <Breadcrumbs>
          <Label text='Consultar Oitiva' bold v={'body1'} />
        </Breadcrumbs>
      </Grid>

      <Grid container direction='column' spacing={2}>
        <Grid item xs={12}>
          <ContainerTitle>
            <Label text='CONSULTAR OITIVA' v='h6' bold />
          </ContainerTitle>
        </Grid>
        <Grid item xs={12}>
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <Grid container item spacing={2}>
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ padding: '1rem' }}>
                    <FormControlRadio
                      name={'type'}
                      label={'Tipo de consulta'}
                      control={control}
                      options={handleFillRadioOptions}
                    />
                  </Paper>
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormInput
                    name={'name'}
                    label={'Nome do adolescente'}
                    placeholder={'Informe o nome do adolescente'}
                    control={control}
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <FormInputDatePicker
                    name={'dateFrom'}
                    label={handleDataInicioLabel(typeWatch)}
                    control={control}
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <FormInputDatePicker name={'dateTo'} label={'Até'} control={control} />
                </Grid>

                {typeWatch !== FilterHearingOptions.OITIVA_EXCUIDA && (
                  <Grid item md={6} xs={12}>
                    <FormInput
                      name={'responsibleFor'}
                      label={handleResponsavelLabel(typeWatch)}
                      placeholder={handleResponsavelPlaceholder(typeWatch)}
                      control={control}
                    />
                  </Grid>
                )}
                {typeWatch !== FilterHearingOptions.OITIVA_EXCUIDA && (
                  <Grid item md={6} xs={12}>
                    <FormInput
                      name={'cnjNumber'}
                      label={'Número CNJ'}
                      placeholder={'Informe o número CNJ'}
                      control={control}
                    />
                  </Grid>
                )}

                <Grid item md={3} xs={12}>
                  <Box display={'flex'} gap={2}>
                    <Button variant={'outlined'} color={'secondary'} onClick={handleReset}>
                      Limpar
                    </Button>
                    <Button
                      type={'submit'}
                      variant={'contained'}
                      color={'secondary'}
                      startIcon={<SearchRoundedIcon />}
                    >
                      Pesquisar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Grid>

        {showMessage ? (
          <Grid item xs={12}>
            <Label bold text={SystemMessages.MESSAGE_020} sx={{ color: 'error.main' }} />
          </Grid>
        ) : (
          data && (
            <Grid item xs={12}>
              {data.length > 0 ? (
                <Stack direction='column' spacing={2}>
                  <Label text='Resultados da Busca:' />
                  <Paper variant='outlined'>
                    <TableData
                      data={data}
                      isExcluded={typeWatch === FilterHearingOptions.OITIVA_EXCUIDA}
                      isFinalized={typeWatch === FilterHearingOptions.OITIVA_FINALIZADA}
                      onDelete={handleDelete}
                      onDownload={handleDownloadHearing}
                      onReactivate={handleReactivate}
                    />
                    <TablePagination
                      labelRowsPerPage='Linhas por página: '
                      labelDisplayedRows={({ from, to, count }) => {
                        return `${from}–${to} de ${count}`
                      }}
                      rowsPerPageOptions={rowsOptions}
                      count={totalOfElements}
                      rowsPerPage={rowsPerPage}
                      onPageChange={(e, p) => handlePage(p)}
                      page={page}
                      component='div'
                      onRowsPerPageChange={(e) => handlePage(0, e)}
                    />
                  </Paper>
                </Stack>
              ) : (
                <Stack direction='row' justifyContent='center' spacing={2}>
                  <Label text='Nenhum resultado encontrado...' bold />
                </Stack>
              )}
            </Grid>
          )
        )}
      </Grid>
    </Layout>
  )
}

export default SearchHearing
